import { Link } from 'react-router-dom';
import { Fragment, ReactNode, useContext, useMemo } from 'react';
import SortRow from './elements/sort-row';
import FilterSidebar from './elements/filter-sidebar';
import Pagination from 'rc-pagination';
import { selectImage } from '../../../util/misc';
import Catalog, { CatalogRenderProps, pageSizes } from '../../catalog';
import { ViewMode } from './catalog-wrapper';
import { CategoriesContext } from '../../../App';

const PaginationItem = (page: number, type: string, element: ReactNode) => {
    switch (type) {
        case 'page': {
            return <div>{page}</div>;
        }
        case 'prev': {
            return (
                <div className="navigation-btns__btn--prev">
                    <svg
                        className="icon"
                        width="12"
                        height="18"
                        viewBox="0 0 12 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M10 16.37L2 9l8-7.369"
                            stroke="#000"
                            strokeWidth="2"
                            strokeLinecap="square"
                        />
                    </svg>
                </div>
            );
        }
        case 'next': {
            return (
                <div className="navigation-btns__btn--next">
                    <svg
                        className="icon"
                        width="12"
                        height="18"
                        viewBox="0 0 12 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M10 16.37L2 9l8-7.369"
                            stroke="#000"
                            strokeWidth="2"
                            strokeLinecap="square"
                        />
                    </svg>
                </div>
            );
        }
        case 'jump-prev':
        case 'jump-next': {
            return <div>...</div>;
        }
    }
    return element;
};

interface CatalogProps {
    viewMode: ViewMode;
    setViewMode: (mode: ViewMode) => void;
    globalMode?: boolean;
}

function NewItkCatalog({ viewMode, setViewMode, globalMode = false }: CatalogProps) {
    const { currentCategory } = useContext(CategoriesContext);
    const render = useMemo(
        () =>
            ({
                query,
                sortBy,
                sortOrder,
                products,
                productsEnd,
                productsStart,
                productsTotal,
                page,
                pageSize,
                classes,
                classId,
                filters,
                filterValues,
                onSortChange,
                setPageSize,
                setPage,
                setQuery,
                setClassId,
                toggleFilterValue,
                resetFilters,
            }: CatalogRenderProps) =>
                (
                    <>
                        <div className="catalog__content">
                            {query && (
                                <h2 className="catalog-query-title">
                                    Результаты поиска по запросу «{query}»:
                                </h2>
                            )}
                            <div className="catalog-sort-row">
                                <SortRow
                                    sortBy={sortBy}
                                    sortOrder={sortOrder}
                                    onSortChange={onSortChange}
                                    viewMode={viewMode}
                                    setViewMode={setViewMode}
                                />
                            </div>

                            <div
                                className={
                                    'catalog__grid catalog_main_section_wrapper_items' +
                                    (viewMode === ViewMode.List ? ' catalog__grid--layout-2' : '')
                                }
                            >
                                {products.map(({ name, etim, imageUrl, imageVariants, url, article }) => {
                                    const maxWidth = viewMode === ViewMode.List ? 100 : 200;
                                    const imgUrl = selectImage(imageVariants, maxWidth, 100, imageUrl);
                                    return (
                                        <div key={article} className="product-item">
                                            <Link to={url}>
                                                <div className="product-item__wrapper">
                                                    <div className="product-item__img-cont">
                                                        <img
                                                            className="product-item__img"
                                                            src={imgUrl}
                                                            alt={name}
                                                        />
                                                    </div>
                                                    <div className="product-item__content">
                                                        <p className="product-item__txt">{name}</p>
                                                        <div className="product-item__parameters">
                                                            <span
                                                                className="product-item__parameters-title product-item__txt-gray"
                                                                style={{ color: '#000' }}
                                                            >
                                                                Артикул: {article}
                                                            </span>
                                                            <div className="product-item__parameters-cont">
                                                                {etim?.features?.slice(0, 5).map((f) => (
                                                                    <Fragment key={f.id}>
                                                                        <span>{f.name}:</span>
                                                                        <span className="product-item__parameter product-item__txt-gray">
                                                                            {f.value}
                                                                        </span>
                                                                    </Fragment>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Link>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="navigation">
                                <div className="navigation-cont navigation__cont--column">
                                    <div className="navigation-items">
                                        <div className="navigation-items__item">
                                            <span className="navigation__txt">
                                                <span>Показано:</span>&nbsp;
                                                <b>
                                                    {productsStart} - {productsEnd}
                                                </b>
                                                &nbsp; (всего <b>{productsTotal}</b>)
                                            </span>
                                        </div>
                                        <div className="navigation-items__item">
                                            <span className="navigation__txt">Показывать по: &nbsp;</span>
                                            <select
                                                value={pageSize}
                                                onChange={(e) => setPageSize(parseInt(e.target.value))}
                                                className="ui-pagination__size-select"
                                            >
                                                {pageSizes.map((size) => (
                                                    <option key={size} value={size}>
                                                        {size}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <Pagination
                                        className="navigation-btns"
                                        pageSize={pageSize}
                                        current={page}
                                        showLessItems
                                        onChange={(page) => {
                                            document.body.scrollTop = 0;
                                            document.documentElement.scrollTop = 0;
                                            setPage(page);
                                        }}
                                        total={productsTotal}
                                        itemRender={PaginationItem}
                                        showTitle={false}
                                    />
                                </div>
                            </div>
                            {currentCategory?.seoText ? (
                                <div
                                    className="catalog-description__txt"
                                    dangerouslySetInnerHTML={{ __html: currentCategory.seoText }}
                                />
                            ) : (
                                currentCategory?.description && (
                                    <div className="catalog-description__txt">
                                        {currentCategory.description.split('\n').map((t, idx) => (
                                            <p key={idx}>{t}</p>
                                        ))}
                                    </div>
                                )
                            )}
                        </div>
                        <FilterSidebar
                            showSearch={!globalMode}
                            query={query}
                            setQuery={setQuery}
                            filters={filters}
                            filterValues={filterValues}
                            classes={classes}
                            classId={classId}
                            setClassId={setClassId}
                            toggleFilterValue={toggleFilterValue}
                            resetFilters={resetFilters}
                        />
                    </>
                ),
        [globalMode, setViewMode, viewMode, currentCategory]
    );
    return <Catalog render={render} />;
}

export default NewItkCatalog;
